import { inject, Injectable } from '@angular/core';
import { NgxAimService } from '../ngx-aim.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { AppStateService } from './app-state.service';
import {
  AimBaseResponse,
  GetCustomerStationRateDto,
  Location,
  ReserveStation,
  Station,
  StationCartItem,
  StationGroup,
  StationGroupRes,
  StationItem,
  StationRate,
  StationRes,
  StationTime
} from '../models';

const STATION_GROUP_KEY = 'station-group';
const STATION_DATE_KEY = 'station-date';
const STATION_DURATION_KEY = 'station-duration';
const STATION_STATION_KEY = 'station-station';
const STATION_TIME_KEY = 'station-time';
const STATION_LOCATION_KEY = 'station-location';
const STATION_RSO_ITEM_KEY = 'station-rso-item';
const STATION_ADD_ON_ITEMS_KEY = 'statoin-add-on-items';
const STATION_AMOUNT_OF_GUESTS_KEY = 'station-amount-of-guests';
const STATION_RATE_KEY = 'station-rate';
const STATION_NEEDS_RSO_KEY = 'station-needs-rso';
const STATION_RSO_ITEM = 'station-rso-item';
const STATION_GUEST_ITEM = 'station-guest-item';

@Injectable({
  providedIn: 'root'
})
export class StationsService {
  ngxAimService = inject(NgxAimService);
  http = inject(HttpClient);
  appStateService = inject(AppStateService);

  private baseUrl = `${this.ngxAimService.getApiUrl()}/stations`;

  private selectedGroup: StationGroup | null = null;
  private selectedDate: Date | null = null;
  private selectedDurationMinutes: number | null = null;
  private selectedStation: Station | null = null;
  private selectedTime: StationTime | null = null;
  private selectedLocation: Location | null = null;
  private selectedAddonItems: StationCartItem[] = [];
  private selectedAmountOfGuests = 0;
  private selectedRate: StationRate | null = null;
  private needsRangeSafetyOfficer = false;
  private rangeSafetyOfficerItem: StationCartItem | null = null;
  private guestItem: StationCartItem | null = null;
  amountOfStationsInGroup = 0;
  Notes = '';
  allowedGuetsMessage = '';
  skipDurationSelect = false;
  addButtonForAllDay = false;
  stationHasTerms = false;
  hasAcknowledgedAllTerms = false;
  showAddOns = false;

  constructor() {
    if (this.appStateService.isBrowser) {
      this.setGroupFromLocalStorage();
      this.setDateFromLocalStorage();
      this.setDurationFromLocalStorage();
      this.setStationFromLocalStorage();
      this.setTimeFromLocalStorage();
      this.setLocationFromLocalStorage();
      this.setRSOFromLocalStorage();
      this.setAddOnsFromLocalStorage();
      this.setAmountOfGuestsFromLocalStorage();
      this.setRateFromLocalStorage();
      this.setNeedsRSOFromLocalStorage();
      this.setRSOFromLocalStorage();
      this.setRSOItemFromLocalStorage();
      this.setGuestItemFromLocalStorage();
    }
  }

  findAllStationGroups(LocationId: number): Observable<StationGroup[]> {
    return this.http.get<StationGroupRes[]>(`${this.baseUrl}/groups/${LocationId}`).pipe(map(groupsRes => {
      const groups: StationGroup[] = [];
      groupsRes.forEach((groupRes) => {
        const disabledDates: Date[] = [];
        groupRes.disabledDates.forEach((dateString) => {
          disabledDates.push(new Date(`${dateString.split('T')[0]}T00:00:00`));
        });
        const group: StationGroup = {
          ...groupRes,
          minDate: new Date(groupRes.minDate),
          maxDate: new Date(groupRes.maxDate),
          disabledDates,
        }
        groups.push(group);
      });
      return groups;
    }));
  }

  findAllStations(GroupId: number, GenerateAvailabilityCalendar: boolean): Observable<Station[]> {
    return this.http.get<StationRes[]>(`${this.baseUrl}/group/${GroupId}`, {
      params: new HttpParams()
        .set('date', this.selectedDate?.toISOString() || '')
        .set('GenerateAvailabilityCalendar', GenerateAvailabilityCalendar)
        .set('duration', this.selectedDurationMinutes || '')
    }).pipe(map(stationsRes => {
      const stations: Station[] = [];
      stationsRes.forEach((stationRes) => {
        const disabledDates: Date[] = [];
        stationRes.disabledDates.forEach((dateString) => {
          disabledDates.push(new Date(`${dateString.split('T')[0]}T00:00:00`));
        });
        const station: Station = {
          ...stationRes,
          disabledDates,
        }
        stations.push(station);
      });
      return stations;
    }));
  }

  findOneStation(GroupId: number, StationId: number, GenerateAvailabilityCalendar: boolean): Observable<Station> {
    return this.http.get<StationRes>(`${this.baseUrl}/station/${GroupId}/${StationId}`, {
      params: new HttpParams()
        .set('GenerateAvailabilityCalendar', GenerateAvailabilityCalendar)
    }).pipe(map(stationRes => {
      const disabledDates: Date[] = [];
      stationRes.disabledDates.forEach((dateString) => {
        disabledDates.push(new Date(`${dateString.split('T')[0]}T00:00:00`));
      });
      const station: Station = {
        ...stationRes,
        disabledDates,
      }

      return station;
    }));
  }

  completeReservation(): Observable<AimBaseResponse> {
    const dto: ReserveStation = {
      StationId: this.selectedStation!.Id,
      Start: this.selectedTime!.time,
      Length: this.selectedDurationMinutes!,
      Guests: this.selectedAmountOfGuests,
      Notes: this.Notes,
      needsRangeSafetyOfficer: this.needsRangeSafetyOfficer,
    }

    return this.http.post<AimBaseResponse>(`${this.baseUrl}/reserve`, dto)
  }

  getStationItems(): Observable<StationItem[]> {
    return this.http.get<StationItem[]>(`${this.baseUrl}/items`, {
      params: new HttpParams()
        .set('LocationId', this.selectedLocation!.Id),
    });
  }

  setSelectedLocation(location: Location) {
    this.selectedLocation = location;
    localStorage.setItem(STATION_LOCATION_KEY, JSON.stringify(location));
  }

  getSelectedLocation() {
    return this.selectedLocation;
  }

  private setLocationFromLocalStorage() {
    const locationString = localStorage.getItem(STATION_LOCATION_KEY);
    if (locationString) {
      this.setSelectedLocation(JSON.parse(locationString) as Location);
    }
  }

  setSelectedGroup(group: StationGroup) {
    this.selectedGroup = group;
    localStorage.setItem(STATION_GROUP_KEY, JSON.stringify(group));
  }

  getSelectedGroup() {
    return this.selectedGroup;
  }

  private setGroupFromLocalStorage() {
    const selectedGroup = localStorage.getItem(STATION_GROUP_KEY);
    if (selectedGroup) {
      const groupRes = JSON.parse(selectedGroup) as StationGroupRes;
      const disabledDates: Date[] = [];
      groupRes.disabledDates.forEach((dateString) => {
        disabledDates.push(new Date(`${dateString.split('T')[0]}T00:00:00`));
      });
      const group: StationGroup = {
        ...groupRes,
        minDate: new Date(groupRes.minDate),
        maxDate: new Date(groupRes.maxDate),
        disabledDates,
      }
      this.setSelectedGroup(group);
    }
  }

  setSelectedDate(date: Date) {
    this.selectedDate = date;
    localStorage.setItem(STATION_DATE_KEY, date.toISOString());
  }

  getSelectedDate() {
    return this.selectedDate;
  }

  private setDateFromLocalStorage() {
    const dateString = localStorage.getItem(STATION_DATE_KEY);
    if (dateString) {
      this.setSelectedDate(new Date(dateString))
    }
  }

  setSelectedDuration(durationMinutes: number) {
    this.selectedDurationMinutes = durationMinutes;
    localStorage.setItem(STATION_DURATION_KEY, `${durationMinutes}`);
  }

  getSelectedDuration() {
    return this.selectedDurationMinutes;
  }

  private setDurationFromLocalStorage() {
    const durationString = localStorage.getItem(STATION_DURATION_KEY);
    if (durationString) {
      this.setSelectedDuration(+durationString)
    }
  }

  setSelectedStation(station: Station) {
    this.selectedStation = station;
    localStorage.setItem(STATION_STATION_KEY, JSON.stringify(this.selectedStation));
  }

  getSelectedStation() {
    return this.selectedStation;
  }

  private setStationFromLocalStorage() {
    const stationString = localStorage.getItem(STATION_STATION_KEY);
    if (stationString) {
      const stationRes = JSON.parse(stationString) as StationRes;
      const disabledDates: Date[] = [];
      stationRes.disabledDates.forEach((dateString) => {
        disabledDates.push(new Date(`${dateString.split('T')[0]}T00:00:00`));
      });
      const station: Station = {
        ...stationRes,
        disabledDates,
      }
      this.setSelectedStation(station);
    }
  }

  setSelectedTime(time: StationTime) {
    this.selectedTime = time;
    localStorage.setItem(STATION_TIME_KEY, JSON.stringify(time));
  }

  getSelectedTime() {
    return this.selectedTime;
  }

  private setTimeFromLocalStorage() {
    const time = localStorage.getItem(STATION_TIME_KEY);
    if (time) {
      this.setSelectedTime(JSON.parse(time));
    }
  }

  setSelectedRangeSafetyOfficer(rsoItem: StationCartItem) {
    this.rangeSafetyOfficerItem = rsoItem;
    localStorage.setItem(STATION_RSO_ITEM_KEY, JSON.stringify(rsoItem));
  }

  getSelectedRangeSafteyOfficer() {
    return this.rangeSafetyOfficerItem;
  }

  private setRSOFromLocalStorage() {
    const rsoItemString = localStorage.getItem(STATION_RSO_ITEM_KEY);
    if (rsoItemString) {
      this.setSelectedRangeSafetyOfficer(JSON.parse(rsoItemString));
    }
  }

  setSelectedAddOns(items: StationCartItem[]) {
    this.selectedAddonItems = items;
    localStorage.setItem(STATION_ADD_ON_ITEMS_KEY, JSON.stringify(items));
  }

  getSelectedAddOns() {
    return this.selectedAddonItems
  }

  private setAddOnsFromLocalStorage() {
    const itemsString = localStorage.getItem(STATION_ADD_ON_ITEMS_KEY);
    if (itemsString) {
      this.setSelectedAddOns(JSON.parse(itemsString));
    }
  }

  setSelectedAmountOfGuests(amountOfGuests: number) {
    this.selectedAmountOfGuests = amountOfGuests;
    localStorage.setItem(STATION_AMOUNT_OF_GUESTS_KEY, JSON.stringify(amountOfGuests));
  }

  getSelectedAmountOfGuests() {
    return this.selectedAmountOfGuests;
  }

  private setAmountOfGuestsFromLocalStorage() {
    const amountOfGuests = localStorage.getItem(STATION_AMOUNT_OF_GUESTS_KEY);
    if (amountOfGuests) {
      this.setSelectedAmountOfGuests(+amountOfGuests);
    }
  }

  setSelectedRate(rate: StationRate) {
    this.selectedRate = rate;
    localStorage.setItem(STATION_RATE_KEY, JSON.stringify(rate));
  }

  getSelectedRate() {
    return this.selectedRate;
  }

  private setRateFromLocalStorage() {
    const rate = localStorage.getItem(STATION_RATE_KEY);
    if (rate) {
      this.setSelectedRate(JSON.parse(rate));
    }
  }

  setSelectedNeedsRSO(needsRSO: boolean) {
    this.needsRangeSafetyOfficer = needsRSO;
    localStorage.setItem(STATION_NEEDS_RSO_KEY, JSON.stringify(needsRSO));
  }

  getSelectedNeedsRSO() {
    return this.needsRangeSafetyOfficer;
  }

  private setNeedsRSOFromLocalStorage() {
    const needsRso = localStorage.getItem(STATION_NEEDS_RSO_KEY);
    if (needsRso) {
      this.setSelectedNeedsRSO(JSON.parse(needsRso));
    }
  }

  setRSOItem(item: StationCartItem) {
    this.rangeSafetyOfficerItem = item;
    localStorage.setItem(STATION_RSO_ITEM, JSON.stringify(item));
  }

  getRSOItem() {
    return this.rangeSafetyOfficerItem;
  }

  private setRSOItemFromLocalStorage() {
    const rsoItem = localStorage.getItem(STATION_RSO_ITEM);
    if (rsoItem) {
      this.setRSOItem(JSON.parse(rsoItem));
    }
  }

  setGuestItem(item: StationCartItem) {
    this.guestItem = item;
    localStorage.setItem(STATION_GUEST_ITEM, JSON.stringify(item));
  }

  getGuestItem() {
    return this.guestItem;
  }

  private setGuestItemFromLocalStorage() {
    const guestItem = localStorage.getItem(STATION_GUEST_ITEM);
    if (guestItem) {
      this.setGuestItem(JSON.parse(guestItem));
    }
  }

  addItem(item: StationCartItem) {
    this.selectedAddonItems.push(item);
    this.setSelectedAddOns(this.selectedAddonItems);
  }

  removeItem(item: StationCartItem) {
    let index = this.selectedAddonItems.findIndex((i) => i.SkuPk && i.SkuPk === item.SkuPk);
    if (index === -1) {
      index = this.selectedAddonItems.findIndex((i) => i.Category === item.Category && i.SubCategory === item.SubCategory);
    }
    if (index != -1) {
      this.setSelectedAddOns(this.selectedAddonItems.slice(index, index));
    }
  }

  getRate(dto: GetCustomerStationRateDto): Observable<StationRate> {
    return this.http.post<StationRate>(`${this.baseUrl}/get-rate`, dto);
  }

  reset() {
    this.selectedGroup = null;
    this.selectedDate = null;
    this.selectedDurationMinutes = null;
    this.selectedStation = null;
    this.selectedTime = null;
    this.selectedLocation = null;
    this.selectedAddonItems = [];
    this.selectedRate = null;
    this.selectedAmountOfGuests = 0;
    this.Notes = '';
    this.allowedGuetsMessage = '';
    this.needsRangeSafetyOfficer = false;
    this.rangeSafetyOfficerItem = null;
    this.guestItem = null;
    this.amountOfStationsInGroup = 0;
    localStorage.removeItem(STATION_GROUP_KEY);
    localStorage.removeItem(STATION_DATE_KEY);
    localStorage.removeItem(STATION_DURATION_KEY);
    localStorage.removeItem(STATION_STATION_KEY);
    localStorage.removeItem(STATION_TIME_KEY);
    localStorage.removeItem(STATION_LOCATION_KEY);
    localStorage.removeItem(STATION_ADD_ON_ITEMS_KEY);
    localStorage.removeItem(STATION_RSO_ITEM_KEY);
    localStorage.removeItem(STATION_AMOUNT_OF_GUESTS_KEY);
    localStorage.removeItem(STATION_RATE_KEY);
    localStorage.removeItem(STATION_NEEDS_RSO_KEY);
    localStorage.removeItem(STATION_RSO_ITEM);
    localStorage.removeItem(STATION_GUEST_ITEM);
  }
}
